import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ThemeToggle } from "@/components/ThemeToggle";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import {
  ShoppingBag,
  Package,
  FolderOpen,
  Users,
  LogOut,
  LayoutDashboard,
  BookOpen,
  Menu,
  Upload,
  Percent,
  KeyRound,
  Database,
  Bell,
  FolderSync,
  Boxes,
  ImageIcon,
  FileDown,
  FileText,
  ClipboardList,
  PackageCheck,
  Route,
  GripVertical,
  Check,
  Star,
  Truck,
  Euro,
  Sparkles,
  Tag,
  UserCog,
  BarChart3,
  AlertTriangle,
  Wifi,
  TrendingUp,
  ShieldCheck,
  MapPin,
} from "lucide-react";
import { Mic } from "lucide-react";

// Sidebar Component
const AdminSidebar = ({ currentPage, isOpen, setIsOpen, onChangePassword }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const allMenuItems = [
    { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, path: "/admin", roles: ["admin"] },
    { id: "agent-dashboard", label: "Dashboard", icon: LayoutDashboard, path: "/agent", roles: ["agent"] },
    { id: "warehouse-dashboard", label: "Dashboard", icon: LayoutDashboard, path: "/warehouse", roles: ["magazziniere"] },
    { id: "agent-clients", label: "Clienti", icon: Users, path: "/agent/clients", roles: ["agent"] },
    { id: "quotes", label: "Preventivi", icon: FileText, path: "/agent/quotes", roles: ["agent"] },
    { id: "agent-orders", label: "Ordini", icon: ClipboardList, path: "/agent/orders", roles: ["agent"] },
    { id: "agent-giri", label: "Giri", icon: Truck, path: "/agent/giri", roles: ["agent"] },
    { id: "products", label: "Prodotti", icon: Package, path: "/admin/products", roles: ["admin"] },
    { id: "categories", label: "Categorie", icon: FolderOpen, path: "/admin/categories", roles: ["admin"] },
    { id: "clients", label: "Clienti", icon: Users, path: "/admin/clients", roles: ["admin"] },
    { id: "quotes", label: "Preventivi", icon: FileText, path: "/admin/quotes", roles: ["admin"] },
    { id: "orders", label: "Ordini", icon: ClipboardList, path: "/admin/orders", roles: ["admin"] },
    { id: "voice-orders", label: "Ordini Vocali", icon: Mic, path: "/admin/voice-orders", roles: ["admin"] },
    { id: "pricing", label: "Prezzi", icon: Percent, path: "/admin/pricing", roles: ["admin"] },
    { id: "client-prices", label: "Sconti Clienti", icon: Star, path: "/admin/client-prices", roles: ["admin"] },
    { id: "estratto-conto", label: "Estratto Conto", icon: Euro, path: "/admin/estratto-conto", roles: ["admin"] },
    { id: "sales-stats", label: "Statistiche Vendite", icon: BarChart3, path: "/admin/sales-stats", roles: ["admin"] },
    { id: "giri-mappa", label: "Mappa presa ordini", icon: MapPin, path: "/admin/giri-mappa", roles: ["admin", "agent"] },
    { id: "clienti-fuori-giri", label: "Clienti fuori Giri", icon: AlertTriangle, path: "/admin/clienti-fuori-giri", roles: ["admin"] },
    { id: "rules", label: "Regole AI", icon: Sparkles, path: "/admin/rules", roles: ["admin"] },
    { id: "users", label: "Utenti", icon: Users, path: "/admin/users", roles: ["admin"] },
    { id: "agents-access", label: "Permessi Agenti", icon: UserCog, path: "/admin/agents-access", roles: ["admin"] },
    { id: "active-sessions", label: "Sessioni Attive", icon: Wifi, path: "/admin/active-sessions", roles: ["admin"] },
    { id: "client-analytics", label: "Analisi Clienti", icon: BarChart3, path: "/admin/client-analytics", roles: ["admin"] },
    { id: "forecast", label: "Previsione Domanda", icon: TrendingUp, path: "/admin/forecast", roles: ["admin"] },
    { id: "haccp", label: "Controlli HACCP", icon: ShieldCheck, path: "/admin/haccp", roles: ["admin"] },
    { id: "invites", label: "Codici Invito", icon: KeyRound, path: "/admin/invites", roles: ["admin"] },
    { id: "import", label: "Importa", icon: Upload, path: "/admin/imports", roles: ["admin"] },
    { id: "photos", label: "Foto", icon: ImageIcon, path: "/admin/photos", roles: ["admin"] },
    { id: "exports", label: "Esportazioni", icon: FileDown, path: "/admin/exports", roles: ["admin", "agent"] },
    { id: "backup", label: "Backup", icon: Database, path: "/admin/backup", roles: ["admin"] },
    { id: "push-status", label: "Notifiche", icon: Bell, path: "/admin/push-status", roles: ["admin"] },
    { id: "dropbox-sync", label: "Dropbox", icon: FolderSync, path: "/admin/dropbox-sync", roles: ["admin"] },
    { id: "giacenze", label: "Giacenze", icon: Boxes, path: "/admin/giacenze", roles: ["admin"] },
    { id: "orders", label: "Ordini", icon: ClipboardList, path: "/warehouse/orders", roles: ["magazziniere"] },
    { id: "rounds", label: "Giri Consegne", icon: Route, path: "/warehouse/delivery-rounds", roles: ["magazziniere"] },
    { id: "picking", label: "Lista di Scarico", icon: PackageCheck, path: "/warehouse/picking", roles: ["magazziniere"] },
  ];

  const menuItems = allMenuItems.filter(item => item.roles.includes(user?.role));

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}
      <aside className={`sidebar fixed left-0 top-0 bottom-0 w-64 flex flex-col z-50 transform transition-transform duration-300 lg:translate-x-0 ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}>
      {/* Logo */}
      <div className="p-6 border-b border-white/5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
            <ShoppingBag className="w-6 h-6 text-black" />
          </div>
          <div>
            <h1 className="font-heading text-lg font-bold text-white">Triolo.Store</h1>
            <span className="text-xs text-zinc-500">{user?.role === "agent" ? "Pannello Agente" : user?.role === "magazziniere" ? "Pannello Magazzino" : "Admin Panel"}</span>
          </div>
        </div>
      </div>

      {/* Navigation - scrollable */}
      <nav className="flex-1 p-4 overflow-y-auto">
        <ul className="space-y-1">
          {menuItems.map((item) => (
            <li key={item.id}>
              <Link
                to={item.path}
                data-testid={`nav-${item.id}`}
                onClick={() => setIsOpen(false)}
                className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors text-sm ${
                  currentPage === item.id
                    ? "bg-amber-500/10 text-amber-500"
                    : "text-zinc-400 hover:text-white hover:bg-white/5"
                }`}
              >
                <item.icon className="w-4 h-4" />
                <span>{item.label}</span>
              </Link>
            </li>
          ))}
          {/* Catalog Link inline */}
          <li>
            <Link
              to="/catalog"
              data-testid="nav-catalog"
              onClick={() => setIsOpen(false)}
              className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-zinc-400 hover:text-white hover:bg-white/5 transition-colors text-sm"
            >
              <BookOpen className="w-4 h-4" />
              <span>Visualizza Catalogo</span>
            </Link>
          </li>
        </ul>
      </nav>

      {/* User Info & Logout - compact */}
      <div className="p-3 border-t border-white/5 shrink-0">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center shrink-0">
            <span className="text-purple-400 font-medium text-sm">
              {user?.name?.charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">{user?.name}</p>
            <p className="text-[10px] text-zinc-500 truncate">{user?.email}</p>
          </div>
        </div>
        <Button
          variant="outline"
          data-testid="admin-change-password-btn"
          onClick={() => onChangePassword && onChangePassword()}
          className="w-full h-8 text-xs border-white/10 text-zinc-300 hover:text-amber-400 hover:border-amber-400/50 mb-2"
        >
          <KeyRound className="w-3 h-3 mr-1" />
          Cambia password
        </Button>
        <Button
          variant="outline"
          data-testid="admin-logout-btn"
          onClick={handleLogout}
          className="w-full h-8 text-xs border-white/10 text-zinc-400 hover:text-red-400 hover:border-red-400/50"
        >
          <LogOut className="w-3 h-3 mr-1" />
          Esci
        </Button>
      </div>
    </aside>
    </>
  );
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [editMode, setEditMode] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const allTiles = [
    { id: "products", label: "Prodotti", icon: Package, color: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/20 hover:border-amber-500/40", path: "/admin/products" },
    { id: "categories", label: "Categorie", icon: FolderOpen, color: "text-blue-400", bg: "bg-blue-500/10", border: "border-blue-500/20 hover:border-blue-500/40", path: "/admin/categories" },
    { id: "clients", label: "Clienti", icon: Users, color: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/20 hover:border-emerald-500/40", path: "/admin/clients" },
    { id: "quotes", label: "Preventivi", icon: FileText, color: "text-violet-400", bg: "bg-violet-500/10", border: "border-violet-500/20 hover:border-violet-500/40", path: "/admin/quotes" },
    { id: "orders", label: "Ordini", icon: ClipboardList, color: "text-orange-400", bg: "bg-orange-500/10", border: "border-orange-500/20 hover:border-orange-500/40", path: "/admin/orders" },
    { id: "giri", label: "Giri", icon: Truck, color: "text-sky-400", bg: "bg-sky-500/10", border: "border-sky-500/20 hover:border-sky-500/40", path: "/admin/giri" },
    { id: "voice-orders", label: "Ordini Vocali", icon: Mic, color: "text-red-400", bg: "bg-red-500/10", border: "border-red-500/20 hover:border-red-500/40", path: "/admin/voice-orders" },
    { id: "pricing", label: "Prezzi", icon: Percent, color: "text-pink-400", bg: "bg-pink-500/10", border: "border-pink-500/20 hover:border-pink-500/40", path: "/admin/pricing" },
    { id: "client-prices", label: "Sconti Clienti", icon: Star, color: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/20 hover:border-emerald-500/40", path: "/admin/client-prices" },
    { id: "promotions", label: "Promozioni", icon: Tag, color: "text-red-400", bg: "bg-red-500/10", border: "border-red-500/20 hover:border-red-500/40", path: "/admin/promotions" },
    { id: "estratto-conto", label: "Estratto Conto", icon: Euro, color: "text-orange-400", bg: "bg-orange-500/10", border: "border-orange-500/20 hover:border-orange-500/40", path: "/admin/estratto-conto" },
    { id: "sales-stats", label: "Statistiche Vendite", icon: BarChart3, color: "text-cyan-400", bg: "bg-cyan-500/10", border: "border-cyan-500/20 hover:border-cyan-500/40", path: "/admin/sales-stats" },
    { id: "giri-mappa", label: "Mappa presa ordini", icon: MapPin, color: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/20 hover:border-emerald-500/40", path: "/admin/giri-mappa" },
    { id: "clienti-fuori-giri", label: "Clienti fuori Giri", icon: AlertTriangle, color: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/20 hover:border-amber-500/40", path: "/admin/clienti-fuori-giri" },
    { id: "rules", label: "Regole AI", icon: Sparkles, color: "text-violet-400", bg: "bg-violet-500/10", border: "border-violet-500/20 hover:border-violet-500/40", path: "/admin/rules" },
    { id: "users", label: "Utenti", icon: Users, color: "text-cyan-400", bg: "bg-cyan-500/10", border: "border-cyan-500/20 hover:border-cyan-500/40", path: "/admin/users" },
    { id: "agents-access", label: "Permessi Agenti", icon: UserCog, color: "text-sky-400", bg: "bg-sky-500/10", border: "border-sky-500/20 hover:border-sky-500/40", path: "/admin/agents-access" },
    { id: "active-sessions", label: "Sessioni Attive", icon: Wifi, color: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/20 hover:border-emerald-500/40", path: "/admin/active-sessions" },
    { id: "client-analytics", label: "Analisi Clienti", icon: BarChart3, color: "text-fuchsia-400", bg: "bg-fuchsia-500/10", border: "border-fuchsia-500/20 hover:border-fuchsia-500/40", path: "/admin/client-analytics" },
    { id: "forecast", label: "Previsione Domanda", icon: TrendingUp, color: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/20 hover:border-amber-500/40", path: "/admin/forecast" },
    { id: "haccp", label: "Controlli HACCP", icon: ShieldCheck, color: "text-teal-400", bg: "bg-teal-500/10", border: "border-teal-500/20 hover:border-teal-500/40", path: "/admin/haccp" },
    { id: "invites", label: "Codici Invito", icon: KeyRound, color: "text-yellow-400", bg: "bg-yellow-500/10", border: "border-yellow-500/20 hover:border-yellow-500/40", path: "/admin/invites" },
    { id: "import", label: "Centro Import", icon: Upload, color: "text-teal-400", bg: "bg-teal-500/10", border: "border-teal-500/20 hover:border-teal-500/40", path: "/admin/imports" },
    { id: "photos", label: "Foto", icon: ImageIcon, color: "text-rose-400", bg: "bg-rose-500/10", border: "border-rose-500/20 hover:border-rose-500/40", path: "/admin/photos" },
    { id: "exports", label: "Esportazioni", icon: FileDown, color: "text-indigo-400", bg: "bg-indigo-500/10", border: "border-indigo-500/20 hover:border-indigo-500/40", path: "/admin/exports" },
    { id: "backup", label: "Backup", icon: Database, color: "text-slate-400", bg: "bg-slate-500/10", border: "border-slate-500/20 hover:border-slate-500/40", path: "/admin/backup" },
    { id: "push-status", label: "Notifiche", icon: Bell, color: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/20 hover:border-amber-500/40", path: "/admin/push-status" },
    { id: "dropbox-sync", label: "Dropbox", icon: FolderSync, color: "text-blue-400", bg: "bg-blue-500/10", border: "border-blue-500/20 hover:border-blue-500/40", path: "/admin/dropbox-sync" },
    { id: "giacenze", label: "Giacenze", icon: Boxes, color: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/20 hover:border-emerald-500/40", path: "/admin/giacenze" },
    { id: "catalog", label: "Catalogo", icon: BookOpen, color: "text-lime-400", bg: "bg-lime-500/10", border: "border-lime-500/20 hover:border-lime-500/40", path: "/catalog" },
  ];

  const STORAGE_KEY = `triolo_tile_order_${user?.id || "default"}`;

  const getSavedOrder = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const ids = JSON.parse(saved);
        const ordered = ids.map(id => allTiles.find(t => t.id === id)).filter(Boolean);
        const missing = allTiles.filter(t => !ids.includes(t.id));
        return [...ordered, ...missing];
      }
    } catch (e) { /* localStorage parse error - use defaults */ }
    return allTiles;
  };

  const [tiles, setTiles] = useState(getSavedOrder);

  const saveOrder = useCallback((newTiles) => {
    setTiles(newTiles);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newTiles.map(t => t.id)));
  }, [STORAGE_KEY]);

  // Drag handlers
  const handleDragStart = (idx) => {
    setDragIdx(idx);
  };

  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (dragIdx !== null && idx !== dragIdx) {
      setOverIdx(idx);
    }
  };

  const handleDrop = (idx) => {
    if (dragIdx === null || dragIdx === idx) {
      setDragIdx(null);
      setOverIdx(null);
      return;
    }
    const newTiles = [...tiles];
    const [moved] = newTiles.splice(dragIdx, 1);
    newTiles.splice(idx, 0, moved);
    saveOrder(newTiles);
    setDragIdx(null);
    setOverIdx(null);
  };

  const handleDragEnd = () => {
    setDragIdx(null);
    setOverIdx(null);
  };

  // Touch drag support
  const touchState = useRef({ idx: null, el: null, startY: 0, startX: 0 });
  const gridRef = useRef(null);

  const handleTouchStart = (e, idx) => {
    if (!editMode) return;
    const touch = e.touches[0];
    touchState.current = { idx, startY: touch.clientY, startX: touch.clientX };
  };

  const handleTouchMove = useCallback((e) => {
    if (!editMode || touchState.current.idx === null) return;
    e.preventDefault();
    const touch = e.touches[0];
    const grid = gridRef.current;
    if (!grid) return;
    const children = Array.from(grid.children);
    for (let i = 0; i < children.length; i++) {
      const rect = children[i].getBoundingClientRect();
      if (touch.clientX >= rect.left && touch.clientX <= rect.right && touch.clientY >= rect.top && touch.clientY <= rect.bottom) {
        if (i !== touchState.current.idx) {
          setOverIdx(i);
        }
        break;
      }
    }
  }, [editMode]);

  const handleTouchEnd = useCallback(() => {
    if (touchState.current.idx === null || overIdx === null) {
      touchState.current = { idx: null };
      setOverIdx(null);
      return;
    }
    const fromIdx = touchState.current.idx;
    if (fromIdx !== overIdx) {
      const newTiles = [...tiles];
      const [moved] = newTiles.splice(fromIdx, 1);
      newTiles.splice(overIdx, 0, moved);
      saveOrder(newTiles);
    }
    touchState.current = { idx: null };
    setOverIdx(null);
  }, [tiles, overIdx, saveOrder]);

  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;
    grid.addEventListener("touchmove", handleTouchMove, { passive: false });
    grid.addEventListener("touchend", handleTouchEnd);
    return () => {
      grid.removeEventListener("touchmove", handleTouchMove);
      grid.removeEventListener("touchend", handleTouchEnd);
    };
  }, [handleTouchMove, handleTouchEnd]);

  return (
    <div className="min-h-screen bg-[#09090b]">
      <main className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
              <ShoppingBag className="w-6 h-6 text-black" />
            </div>
            <div>
              <h1 className="font-heading text-xl sm:text-2xl font-bold text-white">Triolo.Store</h1>
              <p className="text-xs text-zinc-500">Pannello Amministrazione</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle className="!w-8 !h-8" />
            <Button
              variant="ghost"
              size="sm"
              data-testid="edit-layout-btn"
              onClick={() => setEditMode(!editMode)}
              className={`h-8 text-xs ${editMode ? "bg-amber-500/20 text-amber-400 border-amber-500/40" : "text-zinc-400 hover:text-white border-white/10"} border`}
            >
              {editMode ? <Check className="w-3.5 h-3.5 mr-1" /> : <GripVertical className="w-3.5 h-3.5 mr-1" />}
              {editMode ? "Fatto" : "Riordina"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              data-testid="admin-change-password-btn-dashboard"
              onClick={() => setChangePasswordOpen(true)}
              className="h-8 text-xs border-white/10 text-zinc-300 hover:text-amber-400 hover:border-amber-400/50"
              title="Cambia password"
            >
              <KeyRound className="w-3 h-3" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              data-testid="admin-logout-btn"
              onClick={handleLogout}
              className="h-8 text-xs border-white/10 text-zinc-400 hover:text-red-400 hover:border-red-400/50"
            >
              <LogOut className="w-3 h-3 mr-1" />
              Esci
            </Button>
          </div>
        </div>

        {editMode && (
          <p className="text-xs text-amber-400/80 mb-4 text-center">Trascina i riquadri per riordinarli</p>
        )}

        {/* Navigation Grid */}
        <div
          ref={gridRef}
          className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4"
        >
          {tiles.map((tile, idx) => {
            const isOver = overIdx === idx;
            const isDragging = dragIdx === idx;

            if (editMode) {
              return (
                <div
                  key={tile.id}
                  draggable
                  onDragStart={() => handleDragStart(idx)}
                  onDragOver={(e) => handleDragOver(e, idx)}
                  onDrop={() => handleDrop(idx)}
                  onDragEnd={handleDragEnd}
                  onTouchStart={(e) => handleTouchStart(e, idx)}
                  data-testid={`tile-${tile.id}`}
                  className={`relative flex flex-col items-center justify-center gap-2 sm:gap-3 p-4 sm:p-6 rounded-xl bg-[#18181b] border transition-all cursor-grab active:cursor-grabbing select-none ${
                    isOver
                      ? "border-amber-500 bg-amber-500/10 scale-105"
                      : isDragging
                      ? "opacity-50 border-white/20"
                      : `${tile.border}`
                  }`}
                  style={{ WebkitUserSelect: "none", touchAction: "none" }}
                >
                  <div className="absolute top-1.5 right-1.5">
                    <GripVertical className="w-4 h-4 text-zinc-600" />
                  </div>
                  <div className={`p-3 sm:p-4 rounded-xl ${tile.bg}`}>
                    <tile.icon className={`w-6 h-6 sm:w-7 sm:h-7 ${tile.color}`} />
                  </div>
                  <span className="text-xs sm:text-sm text-zinc-300 font-medium text-center leading-tight">
                    {tile.label}
                  </span>
                </div>
              );
            }

            return (
              <Link
                key={tile.id}
                to={tile.path}
                data-testid={`tile-${tile.id}`}
                className={`group flex flex-col items-center justify-center gap-2 sm:gap-3 p-4 sm:p-6 rounded-xl bg-[#18181b] border ${tile.border} transition-all hover:scale-[1.03] active:scale-95`}
              >
                <div className={`p-3 sm:p-4 rounded-xl ${tile.bg} transition-colors`}>
                  <tile.icon className={`w-6 h-6 sm:w-7 sm:h-7 ${tile.color}`} />
                </div>
                <span className="text-xs sm:text-sm text-zinc-300 font-medium text-center leading-tight group-hover:text-white transition-colors">
                  {tile.label}
                </span>
              </Link>
            );
          })}
        </div>
      </main>
      <ChangePasswordDialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen} />
    </div>
  );
}

export { AdminSidebar };
